import { absUrl } from "@/lib/url";
import { Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { MonthGrid, CalendarLegend } from "@/components/calendar/MonthGrid";
import { MonthLegendList } from "@/components/calendar/MonthLegendList";
import { PrayerCrossLinkCard } from "@/components/calendar/PrayerCrossLinkCard";
import { AdSlot } from "@/components/AdSlot";
import { PdfExportButton } from "@/components/PdfExportButton";
import {
  addDays,
  parseISO,
  daysInMonth,
  calendarYearPath,
  calendarMonthPath,
  calendarDatePath,
} from "@/lib/calendar/dates";
import { getDayInfo } from "@/lib/calendar/info";
import { HIJRI_MONTHS } from "@/lib/calendar/hijri";
import { getHolidaysForMonth } from "@/data/holidays";
import { getHariPentingForMonth } from "@/data/hari-penting";
import { useLocale } from "@/i18n/LocaleContext";
import { translateHolidayName } from "@/i18n/holidays";
import type { Locale } from "@/i18n/types";
import { getWesternZodiac, zodiacSlug } from "@/lib/calendar/zodiac";
import { MONTH_SLUGS_ID } from "@/lib/calendar/dates";
import { buildDateSemantics } from "@/lib/calendar/semantics";
import { SemanticSection } from "@/components/seo/SemanticSection";
import { SourcesNote } from "@/components/SourcesNote";
import { eventSchema } from "@/lib/seo/schema";

export function DatePage({ y, m, d, locale }: { y: number; m: number; d: number; locale?: Locale }) {
  return (
    <SiteShell locale={locale}>
      <DateBody y={y} m={m} d={d} />
    </SiteShell>
  );
}

function DateBody({ y, m, d }: { y: number; m: number; d: number }) {
  const { t, href, locale } = useLocale();
  const info = getDayInfo(y, m, d);
  const prev = parseISO(addDays(info.iso, -1))!;
  const next = parseISO(addDays(info.iso, 1))!;
  const monthName = t.common.months[m - 1];
  const dayName = t.common.days[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  const formattedLong = `${d} ${monthName} ${y}`;
  const monthHolidays = getHolidaysForMonth(y, m);
  const monthHariPenting = getHariPentingForMonth(m);
  const zodiac = getWesternZodiac(m, d);
  const semantics = buildDateSemantics(info);
  // suppress unused warning
  void daysInMonth;

  const ld: Record<string, unknown>[] = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: t.common.home, item: absUrl(href("/")) },
        { "@type": "ListItem", position: 2, name: t.common.calendar, item: absUrl(href("/kalender/")) },
        { "@type": "ListItem", position: 3, name: `${y}`, item: absUrl(href(calendarYearPath(y))) },
        {
          "@type": "ListItem",
          position: 4,
          name: `${monthName} ${y}`,
          item: absUrl(href(calendarMonthPath(y, m))),
        },
        {
          "@type": "ListItem",
          position: 5,
          name: formattedLong,
          item: absUrl(href(calendarDatePath(y, m, d))),
        },
      ],
    },
  ];
  if (info.holidays.length > 0) {
    const evName = translateHolidayName(info.holidays[0].name, locale);
    ld.push(
      eventSchema(
        {
          name: evName,
          date: info.iso,
          url: calendarDatePath(y, m, d),
          description:
            locale === "en"
              ? `${evName} is observed in Indonesia on ${formattedLong}.`
              : `${evName} diperingati di Indonesia pada ${formattedLong}.`,
        },
        locale === "en" ? "en" : "id",
      ),
    );
  }

  return (
    <>
      {ld.map((node, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(node) }}
        />
      ))}
      <nav className="py-4 text-sm text-muted-foreground">
        <Link to={href("/") as never} className="hover:text-primary">
          {t.common.home}
        </Link>{" "}
        /{" "}
        <Link to={href("/kalender/") as never} className="hover:text-primary">
          {t.common.calendar}
        </Link>{" "}
        /{" "}
        <Link to={href(calendarYearPath(y)) as never} className="hover:text-primary">
          {y}
        </Link>{" "}
        /{" "}
        <Link to={href(calendarMonthPath(y, m)) as never} className="hover:text-primary">
          {monthName}
        </Link>{" "}
        / {d}
      </nav>

      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl tracking-tight md:text-5xl">
            {dayName}, {formattedLong}
          </h1>
          <p className="mt-1 text-muted-foreground">
            {info.isTanggalMerah && (
              <span className="mr-2 rounded-md bg-red-date/10 px-2 py-0.5 text-xs font-medium text-red-date">
                {t.date.redLabel}
              </span>
            )}
            {info.isCutiBersama && (
              <span className="mr-2 rounded-md bg-cuti-bersama/10 px-2 py-0.5 text-xs font-medium text-cuti-bersama">
                {t.date.cutiLabel}
              </span>
            )}
            {!info.isTanggalMerah && !info.isCutiBersama && (
              <span>{t.common.workingDay}</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <PdfExportButton mode="day" year={y} month={m} day={d} allowSwitch />
        </div>
      </header>

      <div className="space-y-6">
        <div className="mx-auto w-full max-w-5xl space-y-4">
          {(info.holidays.length > 0 || info.hariPenting.length > 0) && (
            <div className="rounded-lg border border-border bg-card p-4">
              <h2 className="mb-2 font-semibold">{t.date.eventToday}</h2>
              <ul className="space-y-1.5 text-sm">
                {info.holidays.map((h) => (
                  <li key={h.name}>
                    <span
                      className={
                        h.type === "libur"
                          ? "font-medium text-red-date"
                          : "font-medium text-cuti-bersama"
                      }
                    >
                      {h.type === "libur" ? t.common.nationalHolidays : t.common.jointLeave}:
                    </span>{" "}
                    {translateHolidayName(h.name, locale)}
                  </li>
                ))}
                {info.hariPenting.map((h) => (
                  <li key={h.name}>
                    <span className="font-medium text-hari-penting">
                      {t.common.importantDays}:
                    </span>{" "}
                    <Link to={`/hari/${h.slug}/` as never} className="hover:text-primary">
                      {h.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Row A — Calendars (3 cards) */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Card title={t.date.hijri} accent="primary">
              <p className="mt-1 text-lg font-semibold">{info.hijriFormatted}</p>
              <p className="text-xs text-muted-foreground">
                {info.hijri.day} {HIJRI_MONTHS[info.hijri.month - 1]} {info.hijri.year} H
              </p>
              <Link
                to={href(`/kalender-hijriah/${info.hijri.year}/${info.hijri.month}/`) as never}
                className="mt-2 inline-block text-xs text-primary hover:underline"
              >
                Lihat bulan Hijriah →
              </Link>
            </Card>
            <Card title={t.date.jawa}>
              <p className="mt-1 text-lg font-semibold">{info.jawa.weton}</p>
              <p className="text-xs text-muted-foreground">
                Neptu {info.jawa.neptu} • Wuku {info.jawa.wuku}
              </p>
              <p className="text-xs text-muted-foreground">
                {info.jawa.tanggalJawa} {info.jawa.bulanJawa} {info.jawa.tahunJawa} {info.jawa.windu}
              </p>
            </Card>
            <Card title={t.date.bali}>
              <p className="mt-1 text-lg font-semibold">
                {info.bali.saptawara} {info.bali.pancawara}
              </p>
              <p className="text-xs text-muted-foreground">
                Wuku {info.bali.wuku} • {info.bali.triwara}
              </p>
            </Card>
          </div>

          {/* Row B — Cultural (3 cards): Tionghoa | Zodiak | Pranata Mangsa */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Card title={t.date.chinese}>
              <p className="mt-1 text-lg font-semibold">
                {info.china.lunarDay} {info.china.monthName} {info.china.lunarYear}
              </p>
              <p className="text-xs text-muted-foreground">
                {locale === "id" ? "Shio" : "Zodiac"} {info.china.zodiac} • {info.china.element} {info.china.yinYang}
              </p>
              <p className="text-xs text-muted-foreground">{info.china.cyclicalYear}</p>
              {info.china.festivals.length > 0 && (
                <p className="mt-1 text-xs text-hari-penting">
                  {info.china.festivals.join(", ")}
                </p>
              )}
            </Card>
            <Card title={locale === "id" ? "Zodiak" : "Zodiac"} accent="primary">
              <p className="mt-1 text-lg font-semibold">
                {zodiac.symbol} {locale === "id" ? zodiac.nameId : zodiac.nameEn}
              </p>
              <p className="text-xs text-muted-foreground">
                {locale === "id" ? zodiac.elementId : zodiac.elementEn} • {zodiac.ruler} ·{" "}
                {locale === "id" ? zodiac.rangeId : zodiac.rangeEn}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {(locale === "id" ? zodiac.traitsId : zodiac.traitsEn).slice(0, 3).join(" • ")}
              </p>
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs">
                <Link
                  to={href(`/zodiak/${zodiacSlug(zodiac)}`) as never}
                  className="text-primary hover:underline"
                >
                  Profil {locale === "id" ? zodiac.nameId : zodiac.nameEn} →
                </Link>
                <Link
                  to={href(`/zodiak/tanggal/${d}-${MONTH_SLUGS_ID[m - 1]}`) as never}
                  className="text-primary hover:underline"
                >
                  {locale === "id" ? `Lahir ${d} ${monthName}?` : `Born on this date?`}
                </Link>
              </div>
            </Card>
            <Card title={t.date.pranata}>
              <p className="mt-1 text-lg font-semibold">🌾 {info.jawa.pranataMangsa.name}</p>
              <p className="text-xs text-muted-foreground line-clamp-3">
                {info.jawa.pranataMangsa.deskripsi}
              </p>
            </Card>
          </div>

          <CalendarLegend />
          <MonthGrid year={y} month={m} showSubLabels />

          <div className="flex justify-between text-sm">
            <Link
              to={href(calendarDatePath(prev.y, prev.m, prev.d)) as never}
              className="rounded-md border border-border px-3 py-1.5 hover:bg-accent"
            >
              ← {prev.d} {t.common.months[prev.m - 1]}
            </Link>
            <Link
              to={href(calendarDatePath(next.y, next.m, next.d)) as never}
              className="rounded-md border border-border px-3 py-1.5 hover:bg-accent"
            >
              {next.d} {t.common.months[next.m - 1]} →
            </Link>
          </div>

          <MonthLegendList
            year={y}
            month={m}
            holidays={monthHolidays}
            hariPenting={monthHariPenting}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-border bg-card p-4 text-sm">
            <h2 className="mb-2 font-semibold">{t.common.quickNav}</h2>
            <ul className="space-y-1.5">
              <li>
                <Link to={href(calendarMonthPath(y, m)) as never} className="hover:text-primary">
                  {t.date.monthLabel(monthName, y)}
                </Link>
              </li>
              <li>
                <Link to={href(calendarYearPath(y)) as never} className="hover:text-primary">
                  {t.date.yearLabel(y)}
                </Link>
              </li>
              <li>
                <Link to={href("/tools/cek-weton") as never} className="hover:text-primary">
                  {t.date.quickWeton}
                </Link>
              </li>
              <li>
                <Link to={href("/tools/kalkulator-hari") as never} className="hover:text-primary">
                  {t.date.quickCalc}
                </Link>
              </li>
            </ul>
          </div>
          <div className="rounded-lg border border-border bg-card p-4 text-sm">
            <h2 className="mb-2 font-semibold">{t.common.hijriCalendar}</h2>
            <p className="text-muted-foreground">
              {info.hijriFormatted}
            </p>
            <Link
              to={href(`/kalender-hijriah/${info.hijri.year}/${info.hijri.month}/`) as never}
              className="mt-2 inline-block text-primary hover:underline"
            >
              {t.common.viewInHijri}: {HIJRI_MONTHS[info.hijri.month - 1]} {info.hijri.year} H →
            </Link>
          </div>
        </div>
        {locale === "id" && <PrayerCrossLinkCard mode="day" y={y} m={m} d={d} />}
        {locale === "id" && (
          <SemanticSection
            block={semantics}
            heading={`Tentang ${formattedLong}`}
          />
        )}
        {locale === "id" && <SourcesNote variant="kalender" />}
        <AdSlot size="rectangle" position="sidebar" />
      </div>
    </>
  );
}

function Card({
  title,
  children,
  accent,
}: {
  title: string;
  children: React.ReactNode;
  accent?: "primary";
}) {
  const cls =
    "h-full rounded-lg border bg-card p-4 " +
    (accent === "primary" ? "border-primary/30" : "border-border");
  return (
    <div className={cls}>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {children}
    </div>
  );
}